import React from "react";
import {createBrowserRouter, Navigate, redirect} from "react-router-dom";

import {AppRoot} from "AppRoot";
import Email from "pages/Email";
import Internal from "pages/Internal";
import LandingPage from "pages/LandingPage";
import Events from "pages/Events";
import Partners from "pages/Partners";
import Livestream from "pages/Livestream";
import Webinar from "pages/Webinar";
import Social from "pages/Social";
import Ads from "pages/Ads";
import Releases from "pages/Releases";
import Print from "pages/Print";
import Scene from "pages/Scene";
import Video from "pages/Video";
import AdminApp from "pages/Admin";
import Grid from "pages/Grid";
import Projects from "pages/Projects";
import GridRender from "pages/GridRender";
import MyScenes from "pages/MyScenes";
import Login from "pages/Login";
import PointCloudImport from "pages/PointCloudImport";
import FlagsPage from "pages/Admin/FlagsPage";
import UsersPage from "pages/Admin/UsersPage";
import AnalyticsPage from "pages/Admin/AnalyticsPage";
import TemplatesPage from "pages/Admin/TemplatesPage";
import {resolveChannelLandingPath, resolveHomeLandingPath} from "libs/channelLanding";
import {readLastLocation} from "libs/lastLocation";
import {USER_ROLES} from "data/user";

// A bare /<channel> path fills in the product and the content type. The type
// follows the admin-saved Stage order where one exists, so the stage an admin
// puts first is the stage users land on.
const channelRedirect = (channelValue) => async () =>
  redirect(`/${await resolveChannelLandingPath(channelValue)}`);

// Only the first index visit of a page load opens the last location. A later
// visit in the same page load is a redirect from a guard that sent the user
// away, and it opens the default landing. Thus a bad entry cannot start a
// redirect loop.
let isFirstIndexVisit = true;

// Decide index redirect before rendering any components
async function indexLoader() {
  const mayOpenLastLocation = isFirstIndexVisit;
  isFirstIndexVisit = false;
  try {
    const resp = await fetch("/api/auth/me", {credentials: "include"});
    if (!resp.ok) return redirect("/login");
    const {user, role, featureFlags} = await resp.json();
    if (!user) return redirect("/login");
    const viewer = {role: role || USER_ROLES.EXTERNAL, featureFlags};
    // The user comes back to the selectors as they left them (libs/lastLocation.ts).
    const lastLocation = mayOpenLastLocation ? readLastLocation(user.email, viewer) : null;
    if (lastLocation) return redirect(lastLocation);
    return redirect(`/${await resolveHomeLandingPath(viewer)}`);
  } catch (_) {
    return redirect("/login");
  }
}

export const router = createBrowserRouter([
  {path: "grid", element: <Grid />},
  {path: "grid/projects/:projectId", element: <Grid />},
  {path: "grid/:snapshotId", element: <Grid />},
  {path: "projects", element: <Projects />},
  {path: "grid-render", element: <GridRender />},
  {path: "import/point-cloud", element: <PointCloudImport />},
  {
    path: "login",
    element: <Login />,
  },
  {
    // Work history sits at the top level, outside the editor's AppRoot, so a
    // list render needs none of the editor's context providers.
    path: "scenes",
    element: <MyScenes />,
  },
  {
    path: "admin",
    element: <AdminApp />,
    children: [
      {
        path: "flags",
        element: <FlagsPage />,
      },
      {
        path: "users",
        element: <UsersPage />,
      },
      {
        path: "analytics",
        element: <AnalyticsPage />,
      },
      {
        path: "templates",
        element: <TemplatesPage />,
      },
    ],
  },
  {
    path: "/",
    element: <AppRoot />,
    children: [
      {
        index: true,
        loader: indexLoader,
      },
      {
        path: "social",
        loader: channelRedirect("social"),
      },
      {
        path: "livestream",
        loader: channelRedirect("livestream"),
      },
      {
        path: "internal",
        loader: channelRedirect("internal"),
      },
      {
        path: "landingpage",
        loader: channelRedirect("landingpage"),
      },
      {
        path: "partners",
        loader: channelRedirect("partners"),
      },
      {
        path: "email",
        loader: channelRedirect("email"),
      },
      {
        path: "print",
        loader: channelRedirect("print"),
      },
      {
        path: "video",
        loader: channelRedirect("video"),
      },
      {
        path: "events",
        loader: channelRedirect("events"),
      },
      {
        path: "webinar",
        loader: channelRedirect("webinar"),
      },
      {
        path: "releases",
        loader: channelRedirect("releases"),
      },
      {
        path: "ads",
        loader: channelRedirect("ads"),
      },
      {
        path: "scene/:sceneId",
        element: <Scene />,
      },
      {
        path: "social/:product/:contentType",
        element: <Social />,
      },
      {
        path: "releases/:product/:contentType",
        element: <Releases />,
      },
      {
        path: "internal/:product/:contentType",
        element: <Internal />,
      },
      {
        path: "livestream/:product/:contentType",
        element: <Livestream />,
      },
      {
        path: "landingpage/:product/:contentType",
        element: <LandingPage />,
      },
      {
        path: "partners/:product/:contentType",
        element: <Partners />,
      },
      {
        path: "email/:product/:contentType",
        element: <Email />,
      },
      {
        path: "print/:product/:contentType",
        element: <Print />,
      },
      {
        path: "video/:product/:contentType",
        element: <Video />,
      },
      {
        path: "webinar/:product/:contentType",
        element: <Webinar />,
      },
      {
        path: "events/:product/:contentType",
        element: <Events />,
      },
      {
        path: "ads/:product/:contentType",
        element: <Ads />,
      },
    ],
  },
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);
